import { z } from 'zod'

import { AUDIENCE_CHOICES } from '@/pages/announcements/announcement-audiences'
import { PLACEMENT_CHOICES } from '@/pages/announcements/announcement-placements'
import { TAG_MAX_LENGTH } from '@/pages/announcements/components/announcement-form-utils'
import { STATUS_FILTER_CHOICES } from '@/pages/announcements/components/announcement-schedule'
import { trimmedTextParam } from '@/utils/search-params'

// NOT `z.coerce.boolean()`: that runs `Boolean(value)`, so a hand-typed
// `?include_expired=false` would coerce to `true` and silently invert the
// filter. The router serialises our own value as a JSON boolean; the string
// arm covers a URL typed or pasted by hand.
const includeExpiredSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((value) => value === true || value === 'true')

// An out-of-range placement is DROPPED, not forwarded. The endpoint answers an
// unknown one with a 422, so passing it through would turn a mistyped or
// outdated link into an error page — where dropping it degrades to the
// unfiltered list the reader can then narrow themselves.
const placementSchema = z.enum(PLACEMENT_CHOICES).optional().catch(undefined)

// Dropped when out of range, for the same reason as `placement`: the BE enum
// answers an unknown status with a 422.
const statusSchema = z.enum(STATUS_FILTER_CHOICES).optional().catch(undefined)

// Dropped when out of range, for the same reason again: the BE enum answers an
// unknown audience with a 422.
const audienceSchema = z.enum(AUDIENCE_CHOICES).optional().catch(undefined)

/**
 * The announcements list keeps its view state in the URL so a staff member can
 * hand a colleague the exact page and filter they are looking at.
 *
 * `page` is 1-based here — what the address bar shows — and the page converts
 * it to the BE's `offset`.
 *
 * Lives beside the page rather than inside `routes/` so the route declaration
 * and its tests read the one definition.
 */
export const announcementsSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional(),
  include_expired: includeExpiredSchema.optional(),
  placement: placementSchema,
  // An EXACT match on the BE, where blank or omitted means no tag filter.
  tag: trimmedTextParam(TAG_MAX_LENGTH),
  status: statusSchema,
  audience: audienceSchema,
})

export type AnnouncementsSearch = z.infer<typeof announcementsSearchSchema>

/**
 * The search keys that narrow the list to a subset. The order is the order the
 * empty state names them in, and each key is also the word it uses.
 *
 * `include_expired` is not one of them: it widens the default view, so it never
 * explains an empty result on its own.
 */
export const ANNOUNCEMENT_FILTER_KEYS = ['placement', 'tag', 'status', 'audience'] as const

export type AnnouncementFilterKey = (typeof ANNOUNCEMENT_FILTER_KEYS)[number]

/**
 * One change from the filter row. A key set to `undefined` clears that filter,
 * so the patch can only add, replace or remove keys — never touch `page`.
 */
export type AnnouncementFiltersPatch = Partial<
  Pick<AnnouncementsSearch, AnnouncementFilterKey | 'include_expired'>
>

/**
 * Every ended row is expired, so the BE ignores `include_expired` under
 * `status=ended` and always returns the expired rows. The rule lives next to
 * the schema because it is part of the BE contract, not a display choice.
 */
export const isExpiredForcedOn = (search: Pick<AnnouncementsSearch, 'status'>): boolean =>
  search.status === 'ended'
